/**
 * Enum for Spring Batch execution statuses (shared by jobs and steps)
 */
export const KB_BATCH_TYPE_CODE = {
  FOLDER: 'FILE_PROCESSING_JOB',
  SHAREPOINT: 'SHAREPOINT_IMPORT_JOB'
};

/**
 * Enum for batch parameter type codes
 */
export const BATCH_PARAM_TYPE_CODE = {
  INPUT_DIRECTORY: 'INPUT_DIRECTORY',
  PROCESSED_DIRECTORY: 'PROCESSED_DIRECTORY',
  ERROR_DIRECTORY: 'ERROR_DIRECTORY',
  KNOWLEDGE_BASE_CODE: 'KNOWLEDGE_BASE_CODE',
  CHUNK_SIZE: 'CHUNK_SIZE'
};

export function getBatchIcon(BatchTypeCode) {
  switch (BatchTypeCode) {
    case KB_BATCH_TYPE_CODE.FOLDER:
      return 'folder';
    case KB_BATCH_TYPE_CODE.SHAREPOINT:
      return 'cloud_download_rounded_icon';
    default:
      return 'default';
  }
}

export function getBatchColor(BatchTypeCode) {
  switch (BatchTypeCode) {
    case KB_BATCH_TYPE_CODE.FOLDER:
      return '#2F678D';
    case KB_BATCH_TYPE_CODE.SHAREPOINT:
      return '#4052AB';
    default:
      return 'default';
  }
}

/**
 * Enum for Spring Batch execution statuses (shared by jobs and steps)
 */
export const BATCH_STATUS = {
  ABANDONED: 'ABANDONED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  STARTED: 'STARTED',
  STARTING: 'STARTING',
  STOPPED: 'STOPPED',
  STOPPING: 'STOPPING',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Utility functions for batch status management
 */
export const BatchUtils = {
  /**
   * Check if a status indicates the execution is finished
   * @param {string} status - The status to check
   * @returns {boolean} True if the status is finished
   */
  isFinished: (status) => {
    return [
      BATCH_STATUS.COMPLETED,
      BATCH_STATUS.FAILED,
      BATCH_STATUS.STOPPED,
      BATCH_STATUS.ABANDONED
    ].includes(status);
  },

  /**
   * Check if a status indicates the execution is running
   * @param {string} status - The status to check
   * @returns {boolean} True if the status is running
   */
  isRunning: (status) => {
    return [BATCH_STATUS.STARTED, BATCH_STATUS.STARTING, BATCH_STATUS.STOPPING].includes(status);
  },

  /**
   * Check if a status indicates success
   * @param {string} status - The status to check
   * @returns {boolean} True if the status is successful
   */
  isSuccessful: (status) => {
    return status === BATCH_STATUS.COMPLETED;
  },

  /**
   * Check if a status indicates failure
   * @param {string} status - The status to check
   * @returns {boolean} True if the status indicates failure
   */
  isFailed: (status) => {
    return [BATCH_STATUS.FAILED, BATCH_STATUS.ABANDONED].includes(status);
  },

  /**
   * Get color theme for status
   * @param {string} status - The status
   * @returns {string} The color theme
   */
  getStatusColor: (status) => {
    switch (status) {
      case BATCH_STATUS.STARTED:
      case BATCH_STATUS.STARTING:
        return 'primary';
      case BATCH_STATUS.COMPLETED:
        return 'success';
      case BATCH_STATUS.FAILED:
      case BATCH_STATUS.ABANDONED:
        return 'error';
      case BATCH_STATUS.STOPPED:
      case BATCH_STATUS.STOPPING:
        return 'warning';
      default:
        return 'default';
    }
  },

  /**
   * Get icon name for status
   * @param {string} status - The status
   * @returns {string} The icon component name
   */
  getStatusIcon: (status) => {
    switch (status) {
      case BATCH_STATUS.STARTED:
      case BATCH_STATUS.STARTING:
        return 'play_arrow_icon';
      case BATCH_STATUS.COMPLETED:
        return 'check_circle_icon';
      case BATCH_STATUS.FAILED:
      case BATCH_STATUS.ABANDONED:
        return 'error_icon';
      case BATCH_STATUS.STOPPED:
      case BATCH_STATUS.STOPPING:
        return 'pause_icon';
      default:
        return 'play_arrow_icon';
    }
  }
};

/**
 * All possible batch statuses as an array
 */
export const ALL_BATCH_STATUSES = Object.values(BATCH_STATUS);

/**
 * Grouped statuses for easier filtering
 */
export const BATCH_STATUS_GROUPS = {
  ACTIVE: [BATCH_STATUS.STARTED, BATCH_STATUS.STARTING, BATCH_STATUS.STOPPING],
  FINISHED: [
    BATCH_STATUS.COMPLETED,
    BATCH_STATUS.FAILED,
    BATCH_STATUS.STOPPED,
    BATCH_STATUS.ABANDONED
  ],
  SUCCESS: [BATCH_STATUS.COMPLETED],
  ERROR: [BATCH_STATUS.FAILED, BATCH_STATUS.ABANDONED]
};
